import { getCheckoutIntent } from "@/api/rest/checkoutApi";
import { useMachine } from "@xstate/react";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkoutUIMachine } from "./checkoutUIMachine";
import { useAsyncCheckoutEnabled } from "./hooks/useAsyncCheckoutEnabled";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { getAllCarts } from "@/redux/selectors";
import isEqual from "lodash/isEqual";
import { ShoppingCartItem, UserRetailerShoppingCart } from "../../types";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import { getShareableShoppingCart } from "@/api/rest/checkoutApi";

type CheckoutUIContextType = ReturnType<typeof useMachine<typeof checkoutUIMachine>>;

const CheckoutUIContext = createContext<CheckoutUIContextType | null>(null);

interface CheckoutUIProviderProps {
  children: ReactNode;
}

type ShoppingCartWithItems = Partial<UserRetailerShoppingCart> &
  Pick<Required<UserRetailerShoppingCart>, "items" | "retailerId">;

export const CheckoutUIProvider = ({ children }: CheckoutUIProviderProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchOperationRef = useRef<'idle' | 'in-progress' | 'completed'>('idle');
  const searchParams = new URLSearchParams(window.location.search);
  const checkoutIntentId = searchParams.get("checkoutIntentId");
  const retailerId = searchParams.get("retailerId");
  const retailerSku = searchParams.get("retailerSku");
  const stacklineSku = searchParams.get("stacklineSku");
  const quantity = parseInt(searchParams.get("quantity") || "1", 10);
  const shareShoppingCartId = searchParams.get("shareShoppingCartId");
  const [trackAction] = useTrackActions();

  const checkoutItemsFromUrl = useMemo(() => {
    if (retailerId && retailerSku && stacklineSku) {
      return [
        {
          retailerId: parseInt(retailerId, 10),
          retailerSku,
          stacklineSku,
          quantity,
        },
      ];
    }
    return undefined;
  }, [retailerId, retailerSku, stacklineSku, quantity]);
  const carts = useAppSelector(getAllCarts, isEqual);
  const [shareableShoppingCart, setShareableShoppingCart] = useState<{
    items: ShoppingCartItem[];
  } | null>(null);
  // if shareShoppingCartId is present, we need to wait for the shareable shopping cart to load
  const [shareableShoppingCartLoading, setShareableShoppingCartLoading] = useState(shareShoppingCartId ? true : false);
  const { debug } = useAsyncCheckoutEnabled();

  useEffect(() => {
    if (shareShoppingCartId) {
      setShareableShoppingCartLoading(true);
      getShareableShoppingCart({ shoppingCartId: shareShoppingCartId }).then(({ items }) => {
        setShareableShoppingCart({ items });
        setShareableShoppingCartLoading(false);
      });
    }
  }, [shareShoppingCartId]);

  const itemsToCheckout = useMemo(() => {
    if (checkoutItemsFromUrl && checkoutItemsFromUrl.length > 0) {
      return checkoutItemsFromUrl;
    } else if (shareShoppingCartId) { // if shareShoppingCartId is present, we need to wait for the shareable shopping cart to load
      return shareableShoppingCart?.items.map((item) => ({
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        quantity: item.quantity,
      })) || [];
    } else if (carts.length > 0) {
      let shoppingCartSelected: ShoppingCartWithItems | undefined;
      if (carts.length === 1) {
        shoppingCartSelected = carts[0];
      } else if (retailerId) {
        shoppingCartSelected = carts.find((cart) => cart.retailerId === parseInt(retailerId, 10)) || carts[0];
      }
      if (!shoppingCartSelected && carts.length > 0) {
        shoppingCartSelected = carts[0];
      }
      if (!shoppingCartSelected) {
        return [];
      }
      return shoppingCartSelected.items.map((item) => ({
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        quantity: item.quantity,
      }));
    }
    return [];
  },
    [checkoutItemsFromUrl, carts, retailerId, shareShoppingCartId, shareableShoppingCart]
  );


  // Create a function to handle navigation that will be passed to the context
  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const machine = useMachine(checkoutUIMachine, {
    input: {
      context: {
        navigate: handleNavigate,
        testOrder: debug,
        itemsToCheckout,
      },
    },
  });

  const [state, send] = machine;
  const { context } = state;
  // @ts-ignore
  const { checkoutIntent }: { checkoutIntent: CheckoutIntent } = context;
  const userProfile = useAppSelector((state) => state.userProfile);
  const fetchCheckoutIntent = useCallback(async () => {
    send({
      type: "SET_USER_ID",
      data: { userId: userProfile?.userId },
    });

    // Skip if we're already fetching or have completed; React strict mode will mount this twice
    // and create the checkout intent twice
    if (fetchOperationRef.current !== 'idle' || state.value === 'initial') {
      return;
    }

    // Skip if we already have this checkout intent
    if (checkoutIntent?.checkoutIntentId === checkoutIntentId) {
      fetchOperationRef.current = 'completed';
      return;
    }

    try {
      fetchOperationRef.current = 'in-progress';
      const checkoutIntent = await getCheckoutIntent({ checkoutIntentId, checkoutItems: itemsToCheckout, retailerId: itemsToCheckout?.[0]?.retailerId });
      if (checkoutIntent) {
        // Track the checkout intent creation
        trackAction(AnalyticsTrackingEvent.CHECKOUT_INTENT_CREATED, {
          checkoutIntentId: checkoutIntent.checkoutIntentId,
          retailerId: checkoutIntent.retailerId,
          checkoutItems: checkoutIntent.items || [],
          cartId: checkoutIntent.cartId,
          itemsCount: checkoutIntent.items?.length || 0
        });

        // Send an event to update the context with the checkout intent data
        send({
          type: "SET_CHECKOUT_INTENT",
          data: {
            checkoutIntent,
            itemsToCheckout,
          }
        });

        // Update the URL params with replace
        navigate(
          `/checkout?checkoutIntentId=${checkoutIntent.checkoutIntentId}`,
          { replace: true }
        );
        fetchOperationRef.current = 'completed';
      }
    } catch (error) {
      console.error("Error fetching checkout intent:", error);
      fetchOperationRef.current = 'idle';
      if (
        !checkoutIntentId &&
        (!itemsToCheckout || itemsToCheckout.length === 0)
      ) {
        navigate("/mybag");
      }
    }
  },
    [checkoutIntentId, checkoutIntent, send, navigate, state.value, itemsToCheckout, userProfile?.userId, trackAction]
  );

  useEffect(() => {
    // if userProfile?.userId is present, we need to fetch the checkout intent
    // if checkoutIntent?.userId is not present, we need to fetch the checkout intent
    // if shareShoppingCartId is present, we need to wait for the shareable shopping cart to load
    // if shareShoppingCartId is not present, we need to fetch the checkout intent
    if (userProfile?.userId && checkoutIntent?.userId !== userProfile?.userId && (!shareShoppingCartId || (shareShoppingCartId && !shareableShoppingCartLoading))) {
      fetchCheckoutIntent();
    }
  }, [userProfile?.userId, checkoutIntent, fetchCheckoutIntent, shareableShoppingCartLoading, shareShoppingCartId]);

  useEffect(() => {
    send({
      type: "SET_ITEMS_TO_CHECKOUT",
      data: { itemsToCheckout },
    });
  }, [itemsToCheckout, send]);

  useEffect(() => {
    dispatch(getActiveCarts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.userId]);
  

  return (
    <CheckoutUIContext.Provider value={machine}>
      {children}
    </CheckoutUIContext.Provider>
  );
};

export const useCheckoutUI = () => {
  const context = useContext(CheckoutUIContext);
  if (!context) {
    throw new Error("useCheckoutUI must be used within a CheckoutUIProvider");
  }
  return context;
};
